import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Capacitor } from "@capacitor/core";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import type { LanguageCode } from "./i18n/translations";
import { SubscriptionGate } from "./components/SubscriptionGate";
import { DesktopChatLayout } from "./components/DesktopChatLayout";
import { useDeepLinking } from "./hooks/useDeepLinking";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useProfile } from "./hooks/useProfile";
import { useIsMobile } from "./hooks/use-mobile";
import { MobileAppPrompt } from "./components/MobileAppPrompt";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import Onboarding from "./pages/Onboarding";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import ChatList from "./pages/ChatList";
import ChatScreen from "./pages/ChatScreen";
import AddContact from "./pages/AddContact";
import Settings from "./pages/Settings";
import CreateGroupChat from "./pages/CreateGroupChat";
import Privacy from "./pages/Privacy";
import MicTest from "./pages/MicTest";
import NativeAuthCallback from "./pages/NativeAuthCallback";
import NativeOAuthStart from "./pages/NativeOAuthStart";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const AppRoutes = () => {
  useDeepLinking();
  usePushNotifications();

  // The UI language only otherwise comes from localStorage, which stays
  // stale on a fresh device/reinstall — adopt the signed-in user's saved
  // preferred_language once their profile loads (or changes, e.g. synced
  // from another device) so app text isn't stuck in whatever language this
  // device happened to default to.
  const { profile } = useProfile();
  const { language, setLanguage } = useLanguage();
  useEffect(() => {
    if (profile?.preferred_language && profile.preferred_language !== language) {
      setLanguage(profile.preferred_language as LanguageCode);
    }
  }, [profile?.preferred_language, language, setLanguage]);

  // Apply large-text CSS class based on persisted preference
  useEffect(() => {
    const size = localStorage.getItem('gcc_text_size');
    if (size === 'large') {
      document.documentElement.classList.add('large-text');
    } else {
      document.documentElement.classList.remove('large-text');
    }
  }, []);

  const isMobile = useIsMobile();
  const isNativeApp = Capacitor.isNativePlatform();
  const useDesktopChatLayout = !isNativeApp && !isMobile;

  // Check if current path is an auth-related route that must work on mobile browsers
  // (e.g. when native app opens external browser for OAuth flow)
  const pathname = window.location.pathname;
  const isAuthRoute = pathname.startsWith('/auth/') || pathname === '/~oauth';

  if (isMobile && !isNativeApp && !isAuthRoute) {
    return <MobileAppPrompt />;
  }

  const renderChatRoute = (page: JSX.Element) => (
    <SubscriptionGate>
      {useDesktopChatLayout ? <DesktopChatLayout>{page}</DesktopChatLayout> : page}
    </SubscriptionGate>
  );

  return (
    <Routes>
      <Route path="/" element={<Onboarding />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/~oauth" element={<Auth />} />
      <Route path="/auth/native-callback" element={<NativeAuthCallback />} />
      <Route path="/auth/start-oauth" element={<NativeOAuthStart />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/mic-test" element={<MicTest />} />

      <Route path="/chats" element={renderChatRoute(<ChatList />)} />
      <Route path="/chat/:chatId" element={renderChatRoute(<ChatScreen />)} />
      <Route path="/add-contact" element={renderChatRoute(<AddContact />)} />
      <Route path="/create-group" element={renderChatRoute(<CreateGroupChat />)} />
      <Route path="/settings" element={renderChatRoute(<Settings />)} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider>
        <LanguageProvider>
          <div className="h-screen overflow-hidden">
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <AppErrorBoundary>
                  <AppRoutes />
                </AppErrorBoundary>
              </AuthProvider>
            </BrowserRouter>
          </div>
        </LanguageProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
